import { useQuery } from '@apollo/client';
import {
  IconButton,
  LabelButton,
  Tooltip,
  TooltipProvider,
} from '@teamsnap/snap-ui';
import ParticipantSummaryPanel from 'components/registration/v2/ParticipantSummaryPanel';
import { RegistrationParticipantList } from 'components/registration/v2/RegistrationParticipantList';
import ShareFormDrawer from 'components/registration/v2/ShareFormDrawer';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { REGISTRANT_URL, TEAMSNAP_URL } from 'core/constants';
import * as QueryRegistration from 'core/gql/queries/show-form';
import { ROUTES } from 'core/routes';
import { toDateMED } from 'core/utils';
import { AMPLITUDE_REGISTRATION_ACTIONS, useAmplitude } from 'hooks/amplitude';
import { RegistrationParticipantsProvider } from 'hooks/registrationParticipants';
import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import './registration-details.scss';

type RouteParams = { orgId: string; registrationId: string };

const getBadge = (
  status: 'closed' | 'open',
  closed_at: string,
  opened_at: string
) => {
  if (closed_at) {
    if (status === 'open' && new Date(closed_at) > new Date()) {
      return [`Closes on ${toDateMED(closed_at)}`, 'green'];
    }
    if (status === 'closed' && new Date(closed_at) < new Date()) {
      return [`Closed on ${toDateMED(closed_at)}`, 'grey'];
    }
  }

  if (status === 'closed' && opened_at && new Date(opened_at) > new Date()) {
    return [`Opens on ${toDateMED(opened_at)}`, 'red'];
  }

  return status === 'closed' ? ['Closed', 'grey'] : ['Open', 'green'];
};

const RegistrationDetails: React.FC<RouteComponentProps<RouteParams>> = ({
  match: {
    params: { orgId, registrationId },
  },
}) => {
  const [isShareDrawerOpen, setIsShareDrawerOpen] = React.useState(false);
  const history = useHistory();

  const { amplitudeTrack } = useAmplitude();

  const shareUrl = `${REGISTRANT_URL}/form/${registrationId}`;

  const { data, error } =
    useQuery<QueryRegistration.ShowRegistrationFormResponse>(
      QueryRegistration.QUERY,
      {
        variables: { formId: +registrationId, organizationId: +orgId },
        errorPolicy: 'all',
      }
    );

  const {
    status,
    name,
    closed_at,
    opened_at,
    fields = [],
    season,
  } = data?.showRegistrationForm || {};

  const [text] = getBadge(status, closed_at, opened_at);

  React.useEffect(() => {
    if (error && error.message === 'unauthorized') {
      history.push({
        pathname: ROUTES.FORBIDDEN,
        state: { source: history.location },
      });
    }
  }, [error, history]);

  const amplitudeCopyLink = () => {
    amplitudeTrack.tsbRegistrationActionClicked({
      registration_action: AMPLITUDE_REGISTRATION_ACTIONS.COPY_FORM_LINK,
    });
  };

  const shareForm = () => {
    setIsShareDrawerOpen(true);
    amplitudeTrack.tsbRegistrationActionClicked({
      registration_action: AMPLITUDE_REGISTRATION_ACTIONS.SHARE_FORM,
    });
  };

  const editForm = () => {
    amplitudeTrack.tsbRegistrationActionClicked({
      registration_action: AMPLITUDE_REGISTRATION_ACTIONS.EDIT_FORM,
    });
    history.push(`/organizations/${orgId}/registration/${registrationId}`, {
      prevPath: history.location.pathname,
    });
  };

  return (
    <TooltipProvider>
      <div className="container sui-w-full">
        <div className="page-content sui-p-4">
          <header className="sui-flex sui-justify-between sui-mb-2">
            <div>
              <h1 className="sui-mb-1">{name}</h1>
              <div className="sui-flex sui-gap-1">
                <LabelButton
                  onClick={shareForm}
                  data-testid={'registration-details-share-button'}
                  size="small"
                >
                  {text}
                </LabelButton>
                {season?.ts_division_id && (
                  <a
                    href={`${TEAMSNAP_URL}/${season?.ts_division_id}/league_rostering`}
                    onClick={() => {
                      amplitudeTrack.tsbRegistrationActionClicked({
                        registration_action:
                          AMPLITUDE_REGISTRATION_ACTIONS.GO_TO_ROSTERING,
                      });
                    }}
                  >
                    <LabelButton variantType="secondary" size="small">
                      Go to Rostering
                    </LabelButton>
                  </a>
                )}
                <Tooltip content="Copy registration link to clipboard">
                  <IconButton
                    onClick={() => {
                      amplitudeCopyLink();
                      const cb = navigator.clipboard;
                      cb.writeText(shareUrl).then(() => {
                        reactiveFeedbackMessage({
                          message: 'The registration URL has been copied to your clipboard!',
                          isError: false,
                          autoclose: true,
                        });
                      });
                    }}
                    icon="link"
                    data-testid={'registration-details-copy-link'}
                  />
                </Tooltip>
              </div>
            </div>
            <LabelButton
              data-testid={'registration-details-edit-button'}
              variantType="secondary"
              iconPosition="left"
              icon="edit"
              onClick={editForm}
              labelText="Edit"
            />
          </header>
          <div className="page-content-body">
            <div className="sui-mb-4">
              <ParticipantSummaryPanel
                organizationId={+orgId}
                formId={+registrationId}
              />
            </div>
            <RegistrationParticipantsProvider>
              <RegistrationParticipantList
                fields={fields}
                orgId={orgId}
                formId={registrationId}
              />
            </RegistrationParticipantsProvider>
          </div>
          <ShareFormDrawer
            formIDForSharing={+registrationId}
            setFormIdForSharing={() => console.log('none')}
            isShareDrawerOpen={isShareDrawerOpen}
            setIsShareDrawerOpen={(bool) => setIsShareDrawerOpen(bool)}
            orgId={+orgId}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default RegistrationDetails;
